import { createSlice } from '@reduxjs/toolkit';
// utils
import { callApi } from '../../utils/apiCall';
import { errorHandler } from '../../utils/errorHandler';
import { hasFeatureAccess } from '../../utils/featurePermission';
//
import { dispatch } from '../store';
import { updateError } from './user';
import { setFacebookAdAccount } from './chat';
import { updateDentallyEnabledGlobal } from './practice';

// Extract a readable error message from an API response
const extractErrorMessage = (data, fallback = 'Something went wrong') => {
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    return data?.detail || data?.message || data?.error || fallback;
};

// ---------------------------------------------------------------------
const initialState = {
    isLoading: false,
    practiceUser: {
        results: []
    },
    inviteUser: [],
    message: '',
    error: '',
    stripeConnected: false,
    facebookConnected: false,
    dentallyConfigs: null,
    dentallyLoading: false,
};

const slice = createSlice({
    name: 'practiceIntegration',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
            state.error = '';
            state.message = '';
        },
        stripeConnectedSlice(state, action) {
            state.stripeConnected = action.payload;
        },
        facebookConnectedSlice(state, action) {
            state.facebookConnected = action.payload;
        },
        dentallyConfigsSlice(state, action) {
            state.dentallyConfigs = action.payload;
        },
        dentallyLoadingSlice(state, action) {
            state.dentallyLoading = action.payload;
        },
    }
});

export const { stripeConnectedSlice, reset, facebookConnectedSlice, dentallyConfigsSlice, dentallyLoadingSlice } = slice.actions;

export default slice.reducer;

export function getStripe(practiceId) {
    return async () => {
        if (!hasFeatureAccess('payment-processing')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/stripe/`);
            if (response.status === 200) {
                dispatch(slice.actions.stripeConnectedSlice(response.data.results));
            } else if (response.status !== 403) {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function stripeUrl() {
    return async () => {
        if (!hasFeatureAccess('payment-processing')) return;
        try {
            const response = await callApi('/api/v1/stripe/auth_url/');
            if (response.status === 200) {
                window.location.href = response.data.auth_url;
            } else {
                dispatch(updateError({ success: true, message: response.data.message || 'Something went wrong' }));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function stripePreferenceUrl(data, practiceId, id) {
    return async () => {
        if (!hasFeatureAccess('payment-processing')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/stripe/${id}/`, 'put', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Stripe preference url connected successfully' }));
            } else {
                dispatch(updateError({ success: false, message: response.data?.stripe_account_preference?.checkout_cancel_url?.toString() || response.data?.stripe_account_preference?.checkout_success_url?.toString() || 'Something went wrong' }));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function stripeCallback(data, handlesuccess) {
    return async () => {
        if (!hasFeatureAccess('payment-processing')) return;
        try {
            const response = await callApi('/api/v1/stripe/callback/', 'post', data);
            if (response.status === 200) {
                handlesuccess({ success: true, message: 'Stripe connected successfully' });
            } else {
                handlesuccess({ success: false, message: extractErrorMessage(response?.data) });
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function getFacebook(practiceId) {
    return async () => {
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/facebook-accounts/`);
            if (response.status === 200) {
                dispatch(slice.actions.facebookConnectedSlice(response.data.results));
            } else if (response.status !== 403) {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function facebookUrl(practiceId) {
    return async () => {
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/facebook-accounts/connect/`);
            if (response.status === 200) {
                window.location.href = response.data.auth_url;
            } else {
                dispatch(updateError({ success: true, message: response.data.message || 'Something went wrong' }));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function facebookCallback(practiceId, data, handlesuccess) {
    return async () => {
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/facebook-accounts/callback/`, 'post', data);
            if (response.status === 200) {
                handlesuccess({ success: true, message: 'Facebook connected successfully' });
            } else {
                handlesuccess({ success: false, message: extractErrorMessage(response?.data) });
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function deleteFacebookAccount(practiceId, id, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/facebook-accounts/${id}/disconnect/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Account disconnected successfully!' }));
                handleSuccess({ success: true });
                dispatch(setFacebookAdAccount(''));
            } else {
                dispatch(updateError({ success: false, message: extractErrorMessage(response?.data) }));
                handleSuccess({ success: false, message: extractErrorMessage(response?.data) });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong' }));
        }
    };
}

export function getDentallyConfigs(practiceId) {
    return async () => {
        // Pass practiceId so the gate refuses stale-practice fires during a
        // practice-switch window (otherwise the bootstrap-safe path lets the
        // call through and the BE returns 403 — DENTALSEM-CRM-1BS3 regression).
        if (!hasFeatureAccess('dentally-integration', practiceId)) return;
        try {
            dispatch(slice.actions.dentallyLoadingSlice(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/`);
            if (response.status === 200) {
                dispatch(slice.actions.dentallyConfigsSlice(response.data));
            } else if (response.status !== 403) {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.dentallyLoadingSlice(false));
        }
    };
}

export function updateDentallyConfig(practiceId, configId, data, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration', practiceId)) return;
        try {
            dispatch(slice.actions.dentallyLoadingSlice(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/${configId}/`, 'PATCH', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Dentally configuration updated successfully' }));
                if (onSuccess) onSuccess(response.data);
                dispatch(updateDentallyEnabledGlobal(response.data.is_active));
                dispatch(getDentallyConfigs(practiceId));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.dentallyLoadingSlice(false));
        }
    };
}

export function createDentallyConfig(practiceId, data, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration', practiceId)) return;
        try {
            dispatch(slice.actions.dentallyLoadingSlice(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/`, 'POST', data);
            if (response.status === 200 || response.status === 201) {
                dispatch(updateError({ success: true, message: 'Dentally connected successfully' }));
                if (onSuccess) onSuccess(response.data);
                dispatch(getDentallyConfigs(practiceId));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.dentallyLoadingSlice(false));
        }
    };
}

export function validateDentallyToken(practiceId, onResult) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration', practiceId)) {
            if (onResult) onResult(false);
            return;
        }
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/validate_token/`);
            if (response.status === 200) {
                if (onResult) onResult(response.data?.valid === true);
            } else if (onResult) {
                onResult(false);
            }
        } catch (err) {
            if (onResult) onResult(false);
        }
    };
}
